import '@mui/x-date-pickers/themeAugmentation'
import '@mui/x-date-pickers-pro/themeAugmentation'
import { Components, Theme } from '@mui/material'
import { CalendarOMIcon } from '@omnea-app/icons'

export const MuiDatePicker: Components<Omit<Theme, 'components'>>['MuiDatePicker'] = {
  defaultProps: {
    slots: { openPickerIcon: CalendarOMIcon },
    slotProps: {
      openPickerIcon: {
        size: '18px',
      },
      openPickerButton: {
        color: 'secondary',
        variant: 'ghost',
        size: 'small',
        sx: {
          // Stop the date picker icon from stretching the text field height
          marginY: -0.5,
        },
      },
    },
  },
}

export const MuiPickersPopper: Components<Omit<Theme, 'components'>>['MuiPickersPopper'] = {
  styleOverrides: {
    paper: ({ theme }) => ({
      marginTop: theme.spacing(1.5),
      borderRadius: theme.shape['radius-lg'],
      boxShadow: theme.customShadows.sm,
      borderWidth: theme.strokeWidth.default,
      borderStyle: 'solid',
      borderColor: theme.palette.border.subtle,
      paddingBottom: theme.spacing(4),
      backgroundColor: theme.palette.bg.surface.elevated,
      backgroundImage: 'none', // MUI applies a default background image for Paper
      color: theme.palette.text.default,
    }),
  },
}

export const MuiPickersLayout: Components<Omit<Theme, 'components'>>['MuiPickersLayout'] = {}

const DAY_SIZE_PX = 36
export const MuiDateCalendar: Components<Omit<Theme, 'components'>>['MuiDateCalendar'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      // 7 * days
      // 6 * gap between days
      // 2 * padding on month container
      // 304px
      width: `calc(7 * ${DAY_SIZE_PX}px + 6 * ${theme.spacing(0.5)} + 2 * ${theme.spacing(5)})`,
    }),
  },
}

export const MuiPickersCalendarHeader: Components<Omit<Theme, 'components'>>['MuiPickersCalendarHeader'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(5),
      padding: theme.spacing(0, 5),
      minHeight: 28,
      height: 28,
    }),
    switchViewButton: ({ theme }) => ({
      color: theme.palette.fg.muted,
    }),
  },
}
export const MuiPickersArrowSwitcher: Components<Omit<Theme, 'components'>>['MuiPickersArrowSwitcher'] = {
  styleOverrides: {
    button: ({ theme }) => ({
      color: theme.palette.fg.muted,
    }),
  },
}

export const MuiDayCalendar: Components<Omit<Theme, 'components'>>['MuiDayCalendar'] = {
  styleOverrides: {
    header: ({ theme }) => ({
      margin: theme.spacing(0, 5),
      gap: theme.spacing(0.5),
    }),
    weekDayLabel: ({ theme }) => ({
      ...theme.typography['body-regular'],
      color: theme.palette.text.subtle,
      height: DAY_SIZE_PX,
      margin: 0,
    }),
    weekContainer: ({ theme }) => ({
      margin: theme.spacing(0, 5),
      gap: theme.spacing(0.5),
    }),
    monthContainer: ({ theme }) => ({
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
    }),
    slideTransition: {
      minHeight: 230,
    },
  },
}

export const MuiPickersYear: Components<Omit<Theme, 'components'>>['MuiPickersYear'] = {
  styleOverrides: {
    yearButton: ({ theme }) => ({
      borderRadius: theme.shape['radius-lg'],
      ...theme.typography['body-regular'],

      '&:not(.Mui-disabled):hover': {
        backgroundColor: theme.palette.bg.interactive.hover,
      },

      '&.Mui-selected': {
        backgroundColor: theme.palette.bg.brand.default,
        color: theme.palette.text.brand.onBrand,
        // MUI default sets an override on focused
        '&:focus': {
          backgroundColor: theme.palette.bg.brand.default,
        },
        '&:hover': {
          backgroundColor: theme.palette.bg.brand.hover,
        },
      },
    }),
  },
}

export const MuiPickersDay: Components<Omit<Theme, 'components'>>['MuiPickersDay'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: theme.shape['radius-lg'],

      ...theme.typography['body-regular'],
      color: theme.palette.text.default,

      '&:not(.Mui-disabled):hover': {
        backgroundColor: theme.palette.bg.interactive.hover,
      },

      '&.MuiPickersDay-today:not(.Mui-selected)': {
        borderStyle: 'solid',
        borderWidth: theme.strokeWidth.strong,
        backgroundColor: theme.palette.bg.invisible,
        borderColor: theme.palette.border.strong,
        '&:not(.Mui-disabled):hover': {
          backgroundColor: theme.palette.bg.interactive.hover,
        },
      },
      '&.Mui-selected': {
        border: 'none',
        backgroundColor: theme.palette.bg.brand.default,
        color: theme.palette.text.brand.onBrand,
        // MUI default sets an override on focused
        '&:focus': {
          backgroundColor: theme.palette.bg.brand.default,
        },
        '&:hover': {
          backgroundColor: theme.palette.bg.brand.hover,
        },
      },
      '&.Mui-disabled:not(.Mui-selected)': {
        color: theme.palette.text.subtle,
      },
    }),
    dayWithMargin: ({ theme }) => ({
      margin: theme.spacing(0),
    }),
  },
}

export const MuiDateRangePickerDay: Components<Omit<Theme, 'components'>>['MuiDateRangePickerDay'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      '&:last-of-type': {
        // End of row - selected highlight
        borderTopRightRadius: theme.shape['radius-lg'],
        borderBottomRightRadius: theme.shape['radius-lg'],
        // End of row - preview highlight
        '& .MuiDateRangePickerDay-rangeIntervalPreview': {
          borderTopRightRadius: theme.shape['radius-lg'],
          borderBottomRightRadius: theme.shape['radius-lg'],
        },
      },
      '&:first-of-type': {
        // Start of row - selected highlight
        borderTopLeftRadius: theme.shape['radius-lg'],
        borderBottomLeftRadius: theme.shape['radius-lg'],
        '& .MuiDateRangePickerDay-rangeIntervalPreview': {
          // Start of row - preview highlight
          borderTopLeftRadius: theme.shape['radius-lg'],
          borderBottomLeftRadius: theme.shape['radius-lg'],
        },
      },
    }),
    rangeIntervalDayPreviewStart: ({ theme }) => ({
      // Start of range - preview highlight
      borderTopLeftRadius: theme.shape['radius-lg'],
      borderBottomLeftRadius: theme.shape['radius-lg'],
    }),
    rangeIntervalDayPreviewEnd: ({ theme }) => ({
      // End of range - preview highlight
      borderTopRightRadius: theme.shape['radius-lg'],
      borderBottomRightRadius: theme.shape['radius-lg'],
    }),
  },
}
export const MuiDateRangeCalendar: Components<Omit<Theme, 'components'>>['MuiDateRangeCalendar'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      // Use item spacing not gap to avoid issues with continuous selection background on range
      // Not possible on normal day picker as it doesn't nest days in the same way
      '& .MuiDayCalendar-weekContainer': {
        gap: 0,
      },
      '& .MuiDayCalendar-header': {
        gap: theme.spacing(1),
      },
    }),
  },
}
